import CodeBlock from '../CodeBlock';

const FLOW_DIAGRAM = `Customer          BonesPay API           MoonPay Commerce         Polygon
────────          ────────────           ────────────────         ───────
    │                   │                       │                    │
    │   1. Merchant creates checkout            │                    │
    │   ─────────────────>                      │                    │
    │                   │  2. Create session     │                    │
    │                   │  ─────────────────────>│                    │
    │                   │  3. checkout_url       │                    │
    │                   │  <─────────────────────│                    │
    │   4. Redirect to checkout_url             │                    │
    │   <─────────────────                      │                    │
    │                                           │                    │
    │   5. Customer pays via hosted checkout    │                    │
    │   ───────────────────────────────────────>│                    │
    │                                           │  6. Split on-chain │
    │                                           │  ─────────────────>│
    │                                           │    99% → merchant  │
    │                                           │     1% → treasury  │
    │                   │  7. Webhook: completed │                    │
    │                   │  <─────────────────────│                    │
    │                   │  8. HMAC callback to   │                    │
    │                   │     merchant store     │                    │
    │                   │  ───────────>          │                    │`;

const STEPS = [
  'Your store calls POST /api/checkout/create with the order amount and your wallet address.',
  'BonesPay creates a MoonPay Commerce checkout session with a 99/1 payment split.',
  'You receive a checkout_url and redirect the customer to it.',
  "The customer pays via MoonPay's hosted checkout (card, Apple Pay, etc.).",
  'MoonPay settles on-chain: 99% USDC to your wallet, 1% to BonesPay treasury.',
  'BonesPay receives a webhook from MoonPay, updates the order, and fires an HMAC-signed callback to your store.',
  'Your store confirms the order is paid.',
];

export default function OverviewSection() {
  return (
    <>
      <section id="overview" className="scroll-mt-20 mb-16">
        <span className="font-mono text-[10px] tracking-widest uppercase text-bone-white/30">
          API Reference
        </span>
        <h1 className="font-serif text-bone-white text-3xl md:text-4xl mt-2 mb-4">
          BonesPay API
        </h1>
        <div className="w-12 h-px bg-bone-white/20 mb-6" />

        <div className="space-y-3 text-bone-white/60 font-sans text-sm leading-relaxed">
          <p>
            <span className="font-mono text-xs text-bone-white/40">Base URL:</span>{' '}
            <code className="font-mono text-xs text-bone-white bg-navy-900 px-2 py-0.5 rounded-subtle">
              https://api.bonespay.com
            </code>
          </p>
          <p>
            BonesPay is a payment API that lets merchants accept card payments and settle directly in
            USDC on Polygon. Fund movement is handled entirely by MoonPay Commerce, a licensed money
            services business. BonesPay never custodies, holds, or transmits customer funds. A 1%
            platform fee is split on-chain at payment time.
          </p>
        </div>
      </section>

      <section id="how-it-works" className="scroll-mt-20 mb-16">
        <h2 className="font-serif text-bone-white text-2xl mb-2">How It Works</h2>
        <div className="w-12 h-px bg-bone-white/20 mb-6" />
        <CodeBlock code={FLOW_DIAGRAM} />
        <ol className="mt-6 space-y-2 list-none">
          {STEPS.map((step, i) => (
            <li key={i} className="flex gap-3 text-bone-white/60 font-sans text-sm leading-relaxed">
              <span className="font-mono text-xs text-bone-white/30 flex-shrink-0 w-5 text-right">
                {i + 1}.
              </span>
              <span>{step}</span>
            </li>
          ))}
        </ol>
      </section>

      <section id="authentication" className="scroll-mt-20 mb-16">
        <h2 className="font-serif text-bone-white text-2xl mb-2">Authentication</h2>
        <div className="w-12 h-px bg-bone-white/20 mb-6" />
        <p className="text-bone-white/60 font-sans text-sm leading-relaxed mb-4">
          Authenticated endpoints require the{' '}
          <code className="font-mono text-xs text-bone-white bg-navy-900 px-1.5 py-0.5 rounded-subtle">
            X-Bonespay-Key
          </code>{' '}
          header containing your merchant API key.
        </p>
        <CodeBlock code="X-Bonespay-Key: your_api_key_here" />
        <p className="text-bone-white/50 font-sans text-xs leading-relaxed mt-3">
          You receive your API key when you register via POST /api/merchants/register. It is shown
          only once -- store it securely.
        </p>
      </section>
    </>
  );
}
